import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { Plus, Trash2, Truck, Upload, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EquipmentUpload = ({ carrierEmail, onComplete }) => {
  const { toast } = useToast();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    equipment_type: '',
    description: '',
    registration_number: '',
    registration_expiry: '',
    tare_mass: '',
    payload_rating: '',
    rating_letter_url: '',
    registration_doc_url: '',
    photo_rear_quarter: '',
    photo_front: '',
    photo_side: '',
    axles: '',
    tyres: ''
  });
  const [photoFiles, setPhotoFiles] = useState({
    rear_quarter: null,
    front: null,
    side: null
  });
  const [photoPreviews, setPhotoPreviews] = useState({
    rear_quarter: '',
    front: '',
    side: ''
  });
  const [docFiles, setDocFiles] = useState({
    rating_letter: null,
    registration: null
  });

  useEffect(() => {
    if (carrierEmail) {
      fetchEquipment();
    }
  }, [carrierEmail]);

  const fetchEquipment = async () => {
    try {
      const response = await axios.get(`${API}/equipment/carrier/${carrierEmail}`);
      setEquipment(response.data);
    } catch (error) {
      console.error('Failed to load equipment');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (photoType, file) => {
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Photo size must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid File Type',
          description: 'Only JPG and PNG images are allowed',
          variant: 'destructive'
        });
        return;
      }

      setPhotoFiles(prev => ({ ...prev, [photoType]: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviews(prev => ({ ...prev, [photoType]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocChange = (docType, file) => {
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Document size must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid File Type',
          description: 'Only PDF, JPG, and PNG files are allowed',
          variant: 'destructive'
        });
        return;
      }

      setDocFiles(prev => ({ ...prev, [docType]: file }));
    }
  };

  const validateExpiryDate = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(dateString);
    return expiryDate > today;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.equipment_type || !formData.registration_number || !formData.registration_expiry) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in equipment type, registration number, and expiry date',
        variant: 'destructive'
      });
      return;
    }

    if (!validateExpiryDate(formData.registration_expiry)) {
      toast({
        title: 'Invalid Expiry Date',
        description: 'Registration must not be expired',
        variant: 'destructive'
      });
      return;
    }

    if (!photoFiles.rear_quarter) {
      toast({
        title: 'Missing Photo',
        description: 'Rear 3/4 photo is required',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Convert photos to base64
      const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = error => reject(error);
        });
      };

      const equipmentData = {
        ...formData,
        carrier_email: carrierEmail
      };

      // Add required rear quarter photo
      if (photoFiles.rear_quarter) {
        equipmentData.photo_rear_quarter_base64 = await convertToBase64(photoFiles.rear_quarter);
        equipmentData.photo_rear_quarter_filename = photoFiles.rear_quarter.name;
      }

      // Add optional photos
      if (photoFiles.front) {
        equipmentData.photo_front_base64 = await convertToBase64(photoFiles.front);
        equipmentData.photo_front_filename = photoFiles.front.name;
      }

      if (photoFiles.side) {
        equipmentData.photo_side_base64 = await convertToBase64(photoFiles.side);
        equipmentData.photo_side_filename = photoFiles.side.name;
      }

      // Add optional documents
      if (docFiles.rating_letter) {
        equipmentData.rating_letter_base64 = await convertToBase64(docFiles.rating_letter);
        equipmentData.rating_letter_filename = docFiles.rating_letter.name;
      }

      if (docFiles.registration) {
        equipmentData.registration_doc_base64 = await convertToBase64(docFiles.registration);
        equipmentData.registration_doc_filename = docFiles.registration.name;
      }

      await axios.post(`${API}/equipment`, equipmentData);
      
      toast({
        title: 'Success',
        description: 'Equipment added successfully'
      });
      
      setShowAddForm(false);
      setFormData({
        equipment_type: '',
        description: '',
        registration_number: '',
        registration_expiry: '',
        rating_letter_url: '',
        registration_doc_url: '',
        photo_rear_quarter: '',
        photo_front: '',
        photo_side: ''
      });
      
      fetchEquipment();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add equipment',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (equipmentId) => {
    if (!window.confirm('Are you sure you want to delete this equipment?')) {
      return;
    }

    try {
      await axios.delete(`${API}/equipment/${equipmentId}`);
      toast({
        title: 'Success',
        description: 'Equipment deleted successfully'
      });
      fetchEquipment();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete equipment',
        variant: 'destructive'
      });
    }
  };

  const equipmentTypes = [
    'Prime Mover',
    'Rigid Truck',
    'Semi Trailer',
    'B-Double',
    'Road Train',
    'Flat Top Trailer',
    'Drop Deck Trailer',
    'Low Loader',
    'Curtainsider',
    'Refrigerated Trailer',
    'Tanker',
    'Tipper',
    'Livestock Trailer',
    'Car Carrier',
    'Other'
  ];

  return (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
          <Truck className="w-5 h-5" />
          Equipment Registration & Photos
        </h3>
        <p className="text-sm text-green-800 mb-2">
          Add your equipment with registration details and photos. <strong>At least one piece of equipment is required.</strong>
        </p>
        <ul className="text-sm text-green-800 list-disc ml-5">
          <li><strong>Required:</strong> Rear 3/4 photo of equipment</li>
          <li><strong>Required:</strong> Registration number and expiry date</li>
          <li><strong>Optional:</strong> Rating letters and registration documents</li>
        </ul>
      </div>

      {/* Add Equipment Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Your Equipment ({equipment.length})
        </h3>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Equipment
        </Button>
      </div>

      {/* Add Equipment Form */}
      {showAddForm && (
        <Card className="p-6">
          <h3 className="text-xl font-bold text-[#1e3a5f] mb-6">Add Equipment</h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="equipment_type">Equipment Type *</Label>
                <select
                  id="equipment_type"
                  name="equipment_type"
                  value={formData.equipment_type}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Select equipment type...</option>
                  {equipmentTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="registration_number">Registration Number *</Label>
                <Input
                  id="registration_number"
                  name="registration_number"
                  value={formData.registration_number}
                  onChange={handleChange}
                  placeholder="e.g., ABC123"
                  required
                />
              </div>

              <div>
                <Label htmlFor="registration_expiry">Registration Expiry Date *</Label>
                <Input
                  id="registration_expiry"
                  name="registration_expiry"
                  type="date"
                  value={formData.registration_expiry}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="tare_mass">Tare Mass (kg) *</Label>
                <Input
                  id="tare_mass"
                  name="tare_mass"
                  type="number"
                  value={formData.tare_mass}
                  onChange={handleChange}
                  placeholder="e.g., 8500"
                  required
                />
              </div>

              <div>
                <Label htmlFor="payload_rating">Payload/Rating (kg) *</Label>
                <Input
                  id="payload_rating"
                  name="payload_rating"
                  type="number"
                  value={formData.payload_rating}
                  onChange={handleChange}
                  placeholder="e.g., 12000"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="e.g., 2020 Kenworth T610"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-900 mb-3">Equipment Photos *</h4>
              <p className="text-sm text-gray-600 mb-4">
                Upload photos directly from your computer (JPG or PNG, max 5MB each)
              </p>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="photo_rear_quarter">Rear 3/4 Shot * (Required)</Label>
                  <Input
                    id="photo_rear_quarter"
                    name="photo_rear_quarter"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={(e) => handlePhotoChange('rear_quarter', e.target.files[0])}
                    required
                    className="cursor-pointer"
                  />
                  {photoPreviews.rear_quarter && (
                    <img src={photoPreviews.rear_quarter} alt="Preview" className="mt-2 w-full h-32 object-cover rounded" />
                  )}
                </div>

                <div>
                  <Label htmlFor="photo_front">Front Photo (Optional)</Label>
                  <Input
                    id="photo_front"
                    name="photo_front"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={(e) => handlePhotoChange('front', e.target.files[0])}
                    className="cursor-pointer"
                  />
                  {photoPreviews.front && (
                    <img src={photoPreviews.front} alt="Preview" className="mt-2 w-full h-32 object-cover rounded" />
                  )}
                </div>

                <div>
                  <Label htmlFor="photo_side">Side Photo (Optional)</Label>
                  <Input
                    id="photo_side"
                    name="photo_side"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={(e) => handlePhotoChange('side', e.target.files[0])}
                    className="cursor-pointer"
                  />
                  {photoPreviews.side && (
                    <img src={photoPreviews.side} alt="Preview" className="mt-2 w-full h-32 object-cover rounded" />
                  )}
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-900 mb-3">Documents (Optional)</h4>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rating_letter">Rating Letter Document</Label>
                  <Input
                    id="rating_letter"
                    name="rating_letter"
                    type="file"
                    accept=".pdf,image/jpeg,image/png,image/jpg"
                    onChange={(e) => handleDocChange('rating_letter', e.target.files[0])}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-600 mt-1">Upload rating letter (PDF, JPG, or PNG)</p>
                  {docFiles.rating_letter && (
                    <p className="text-xs text-green-600 mt-1">✓ {docFiles.rating_letter.name}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="registration_doc">Registration Document</Label>
                  <Input
                    id="registration_doc"
                    name="registration_doc"
                    type="file"
                    accept=".pdf,image/jpeg,image/png,image/jpg"
                    onChange={(e) => handleDocChange('registration', e.target.files[0])}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-600 mt-1">Upload registration papers (PDF, JPG, or PNG)</p>
                  {docFiles.registration && (
                    <p className="text-xs text-green-600 mt-1">✓ {docFiles.registration.name}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? 'Adding...' : 'Add Equipment'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Equipment List */}
      <div className="space-y-4">
        {equipment.length === 0 && !showAddForm && (
          <Card className="p-8 text-center">
            <Truck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 mb-4">No equipment added yet</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Equipment
            </Button>
          </Card>
        )}

        {equipment.map((item) => (
          <Card key={item.id} className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-lg font-bold text-gray-900">{item.equipment_type}</h4>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                    {item.registration_number}
                  </span>
                  {item.verified && (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Verified
                    </span>
                  )}
                </div>
                {item.description && (
                  <p className="text-gray-600">{item.description}</p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDelete(item.id)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              {item.photo_rear_quarter && (
                <div>
                  <p className="text-xs text-gray-600 mb-1">Rear 3/4 View</p>
                  <img src={item.photo_rear_quarter} alt="Rear 3/4" className="w-full h-32 object-cover rounded" />
                </div>
              )}
              {item.photo_front && (
                <div>
                  <p className="text-xs text-gray-600 mb-1">Front View</p>
                  <img src={item.photo_front} alt="Front" className="w-full h-32 object-cover rounded" />
                </div>
              )}
              {item.photo_side && (
                <div>
                  <p className="text-xs text-gray-600 mb-1">Side View</p>
                  <img src={item.photo_side} alt="Side" className="w-full h-32 object-cover rounded" />
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Registration Expiry</p>
                <p className={`font-medium ${new Date(item.registration_expiry) < new Date() ? 'text-red-600' : 'text-gray-900'}`}>
                  {new Date(item.registration_expiry).toLocaleDateString()}
                </p>
              </div>
              {item.rating_letter_url && (
                <div>
                  <p className="text-gray-600">Rating Letter</p>
                  <a href={item.rating_letter_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    View Document
                  </a>
                </div>
              )}
              {item.registration_doc_url && (
                <div>
                  <p className="text-gray-600">Registration Document</p>
                  <a href={item.registration_doc_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    View Document
                  </a>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {equipment.length > 0 && onComplete && (
        <Button
          onClick={onComplete}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
        >
          Continue to Payment
        </Button>
      )}
    </div>
  );
};

export default EquipmentUpload;
